import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Edit2, ArrowRight, Music, Loader2, X, HandshakeIcon, DollarSign, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fadeInUp, fadeInLeft } from '../lib/animations';
import {
  EditMessageDialog, MakeOfferDialog, AddAnotherSongDialog, PriceChangeWarningDialog,
} from '../components/cart';

export default function Cart() {
  const navigate = useNavigate();
  const { cart, loading, updateCartItem, removeFromCart, clearCart, checkout, refreshCart, addToCart } = useCart();
  const { ordersEnabled } = usePlatform();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [editingItem, setEditingItem] = useState(null);
  const [editMessage, setEditMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Price change warning state
  const [priceChangeDialogOpen, setPriceChangeDialogOpen] = useState(false);
  const [priceChanges, setPriceChanges] = useState([]);
  const [pendingCheckoutUrl, setPendingCheckoutUrl] = useState(null);
  
  // Offer dialog state
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [selectedArtistForOffer, setSelectedArtistForOffer] = useState(null);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submittingOffer, setSubmittingOffer] = useState(false);

  // Add-another-song dialog state
  const [addAnotherItem, setAddAnotherItem] = useState(null); // existing cart item used as source
  const [addAnotherMessage, setAddAnotherMessage] = useState('');
  const [addAnotherTitle, setAddAnotherTitle] = useState('');
  const [addingAnother, setAddingAnother] = useState(false);
  
  

  // Group items by artist to detect bulk orders
  const itemsByArtist = useMemo(() => {
    const grouped = {};
    (cart.items || []).forEach(item => {
      if (!grouped[item.artist_id]) {
        grouped[item.artist_id] = {
          artist_id: item.artist_id,
          artist_name: item.artist_name,
          artist_image: item.artist_image,
          items: [],
          total: 0
        };
      }
      grouped[item.artist_id].items.push(item);
      grouped[item.artist_id].total += item.price;
    });
    return grouped;
  }, [cart.items]);

  // Check if any artist has 2+ items (eligible for offer)
  const artistsWithBulkOrders = useMemo(() => {
    return Object.values(itemsByArtist).filter(group => group.items.length >= 2);
  }, [itemsByArtist]);

  // toast is a stable module import; searchParams is the only reactive dep
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Checkout cancelled');
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditMessage(item.message);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    const result = await updateCartItem(editingItem.id, editMessage);
    if (result.success) {
      toast.success('Message updated');
      setEditingItem(null);
    } else {
      toast.error(result.error);
    }
  };

  const handleOpenOfferDialog = (artistGroup) => {
    setSelectedArtistForOffer(artistGroup);
    setOfferPrice(artistGroup.total.toFixed(2)); // Start with original total
    setOfferMessage('');
    setOfferDialogOpen(true);
  };

  const handleSubmitOffer = async () => {
    if (!selectedArtistForOffer || !offerPrice) return;
    
    const price = parseFloat(offerPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    setSubmittingOffer(true);
    try {
      const itemIds = selectedArtistForOffer.items.map(i => i.id);
      await api.createOffer(
        selectedArtistForOffer.artist_id,
        itemIds,
        price,
        offerMessage
      );
      
      toast.success('Offer sent!', {
        description: `Your offer of $${price.toFixed(2)} has been sent to ${selectedArtistForOffer.artist_name}`
      });
      
      setOfferDialogOpen(false);
      setSelectedArtistForOffer(null);
      navigate('/dashboard?tab=offers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSubmittingOffer(false);
    }
  };

  const handleOpenAddAnother = (item) => {
    setAddAnotherItem(item);
    setAddAnotherMessage('');
    setAddAnotherTitle('');
  };

  const handleAddAnother = async () => {
    if (!addAnotherItem) return;
    if (!addAnotherTitle.trim()) { toast.error('Please enter the song title'); return; }
    if (!addAnotherMessage.trim()) { toast.error('Please enter a message for your dubplate'); return; }
    setAddingAnother(true);
    const result = await addToCart(addAnotherItem.artist_id, addAnotherMessage, addAnotherItem.agent_id || null, addAnotherTitle);
    setAddingAnother(false);
    if (result.success) {
      toast.success(`Another dubplate added from ${addAnotherItem.artist_name}`);
      setAddAnotherItem(null);
      setAddAnotherMessage('');
      setAddAnotherTitle('');
    } else {
      toast.error(result.error);
    }
  };

  const handleRemoveItem = async (itemId) => {    const result = await removeFromCart(itemId);
    if (result.success) {
      toast.success('Item removed from cart');
    } else {
      toast.error(result.error);
    }
  };

  const handleClearCart = async () => {
    const result = await clearCart();
    if (result.success) {
      toast.success('Cart cleared');
    } else {
      toast.error(result.error);
    }
  };

  const handleCheckoutClick = async () => {
    setCheckoutLoading(true);
    const result = await checkout();
    
    if (result.success && result.data.checkout_url) {
      // Check for price changes
      if (result.data.price_changes && result.data.price_changes.length > 0) {
        setPriceChanges(result.data.price_changes);
        setPendingCheckoutUrl(result.data.checkout_url);
        setPriceChangeDialogOpen(true);
        setCheckoutLoading(false);
      } else {
        window.location.href = result.data.checkout_url;
      }
    } else {
      toast.error(result.error || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleConfirmPriceChange = () => {
    if (pendingCheckoutUrl) {
      window.location.href = pendingCheckoutUrl;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <ShoppingCart className="w-16 h-16 text-lime mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Please log in to view your cart</h1>
          <Link to="/login" className="btn-primary inline-flex items-center gap-2">
            Login <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark text-white">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8">
        <motion.div
          {...fadeInUp}
          className="max-w-4xl mx-auto"
        >
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 text-lime" />
              Your Cart
            </h1>
            {cart.items.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearCart}
                className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Cart
              </Button>
            )}
          </div>

          {loading && cart.items.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-lime" />
            </div>
          ) : cart.items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingCart className="w-20 h-20 text-[#404040] mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
              <p className="text-[#A1A1AA] mb-6">Browse our talented artists and add some dubplates!</p>
              <Link to="/artists" className="btn-primary inline-flex items-center gap-2">
                Browse Artists <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cart Items */}
              <div className="space-y-4">
                {cart.items.map((item, index) => (
                  <motion.div
                    key={item.id}
                    {...fadeInLeft}
                    transition={{ delay: index * 0.1 }}
                    className="bg-surface border border-[#262626] rounded-xl p-4 sm:p-6"
                  >
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Artist Image */}
                      <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-[#1a1a1a] flex-shrink-0">
                        {item.artist_image ? (
                          <img
                            src={item.artist_image}
                            alt={item.artist_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music className="w-8 h-8 text-[#404040]" />
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <Link 
                              to={`/artists/${item.artist_id}`}
                              className="font-semibold text-lg hover:text-lime transition-colors"
                            >
                              {item.artist_name}
                            </Link>
                            {item.song_title && (
                              <p className="text-sm text-white mt-1" data-testid={`song-title-${item.id}`}>
                                <span className="text-[#666]">Song:</span> {item.song_title}
                              </p>
                            )}
                            <p className="text-lime font-bold text-xl mt-1">${item.price}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditItem(item)}
                              className="text-[#A1A1AA] hover:text-lime"
                              data-testid={`edit-item-${item.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-[#A1A1AA] hover:text-red-400"
                              data-testid={`remove-item-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Message Preview */}
                        <div className="mt-3 p-3 bg-[#1a1a1a] rounded-lg">
                          <p className="text-xs text-[#666] mb-1">Your message:</p>
                          <p className="text-sm text-[#A1A1AA] line-clamp-2">{item.message}</p>
                        </div>

                        {/* Inline: Add another song from same artist */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenAddAnother(item)}
                          className="mt-3 h-auto px-2 py-1 text-xs text-lime hover:text-lime hover:bg-lime/10"
                          data-testid={`add-another-${item.id}`}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Add another song from {item.artist_name}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Bulk Order Offers - Show when 2+ items from same artist */}
              {artistsWithBulkOrders.length > 0 && (
                <div className="bg-gradient-to-r from-lime/10 to-transparent border border-lime/30 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <HandshakeIcon className="w-6 h-6 text-lime" />
                    <h3 className="font-semibold text-lg">Bulk Order Discount Available!</h3>
                  </div>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    You have multiple items from the same artist. Make an offer to negotiate a better price!
                  </p>
                  <div className="space-y-3">
                    {artistsWithBulkOrders.map(artistGroup => (
                      <div key={artistGroup.artist_id} className="flex items-center justify-between bg-surface/50 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-[#1a1a1a]">
                            {artistGroup.artist_image ? (
                              <img src={artistGroup.artist_image} alt={artistGroup.artist_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Music className="w-5 h-5 text-[#404040]" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{artistGroup.artist_name}</p>
                            <p className="text-sm text-[#A1A1AA]">
                              {artistGroup.items.length} items • ${artistGroup.total.toFixed(2)} total
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleOpenOfferDialog(artistGroup)}
                          className="btn-primary text-sm"
                          data-testid={`make-offer-${artistGroup.artist_id}`}
                        >
                          <HandshakeIcon className="w-4 h-4 mr-2" />
                          Make Offer
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cart Summary */}
              <div className="bg-surface-highlight border border-[#262626] rounded-xl p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Subtotal ({cart.item_count} items)</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#A1A1AA]">
                    <span>Service Fee (5%)</span>
                    <span>${(cart.total * 0.05).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-[#333] pt-3 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-lime">${(cart.total * 1.05).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link 
                    to="/artists" 
                    className="btn-secondary flex-1 text-center flex items-center justify-center gap-2"
                  >
                    Continue Shopping
                  </Link>
                  <Button
                    onClick={handleCheckoutClick}
                    disabled={!ordersEnabled || checkoutLoading || cart.items.length === 0}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="checkout-btn"
                  >
                    {!ordersEnabled ? (
                      <>Launching Soon</>
                    ) : checkoutLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Checkout <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </main>

      {/* Edit Message Dialog */}
      <EditMessageDialog
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        editMessage={editMessage}
        setEditMessage={setEditMessage}
        handleSaveEdit={handleSaveEdit}
      />

      {/* Make Offer Dialog */}
      <MakeOfferDialog
        open={offerDialogOpen}
        onOpenChange={setOfferDialogOpen}
        selectedArtistForOffer={selectedArtistForOffer}
        offerPrice={offerPrice}
        setOfferPrice={setOfferPrice}
        offerMessage={offerMessage}
        setOfferMessage={setOfferMessage}
        handleSubmitOffer={handleSubmitOffer}
        submittingOffer={submittingOffer}
      />

      {/* Add Another Song Dialog */}
      <AddAnotherSongDialog
        addAnotherItem={addAnotherItem}
        setAddAnotherItem={setAddAnotherItem}
        addAnotherTitle={addAnotherTitle}
        setAddAnotherTitle={setAddAnotherTitle}
        addAnotherMessage={addAnotherMessage}
        setAddAnotherMessage={setAddAnotherMessage}
        handleAddAnother={handleAddAnother}
        addingAnother={addingAnother}
      />

      {/* Price Change Warning Dialog */}
      <PriceChangeWarningDialog
        open={priceChangeDialogOpen}
        onOpenChange={setPriceChangeDialogOpen}
        priceChanges={priceChanges}
        handleConfirmPriceChange={handleConfirmPriceChange}
        handleCancel={() => { setPriceChangeDialogOpen(false); setPendingCheckoutUrl(null); }}
      />

      <Footer />
    </div>
  );
}
